import React from "react";
import { Alert, Box, Chip, CircularProgress, Typography } from "@mui/material";
import { CheckCircle, ErrorOutline, PlayCircle, RadioButtonUnchecked, Build } from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const statusConfig = {
  waiting: { label: "Waiting", icon: <RadioButtonUnchecked fontSize="inherit" />, color: "default" },
  running: { label: "Running", icon: <PlayCircle fontSize="inherit" />, color: "primary" },
  done: { label: "Done", icon: <CheckCircle fontSize="inherit" />, color: "success" },
  skipped: { label: "Not Run", icon: <RadioButtonUnchecked fontSize="inherit" />, color: "default" },
  error: { label: "Error", icon: <ErrorOutline fontSize="inherit" />, color: "error" },
};

const MarkdownBlock = ({ children, markdownSx }) => (
  <Box sx={markdownSx}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{children || ""}</ReactMarkdown>
  </Box>
);

const TeamRunMessage = ({ teamRun, markdownSx, isDarkMode }) => {
  if (!teamRun) return null;

  const members = teamRun.members || [];
  const shouldShowFinal = teamRun.teamMode !== "route" || teamRun.finalAnswer;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {teamRun.error && (
        <Alert severity="error" sx={{ borderRadius: 1 }}>
          {teamRun.error}
        </Alert>
      )}

      {shouldShowFinal && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1.5,
            backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "#ffffff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Final Answer
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={teamRun.teamMode || "team"}
              sx={{ textTransform: "capitalize", fontWeight: 700 }}
            />
          </Box>
          {teamRun.synthesizing && !teamRun.finalAnswer ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="body2" color="text.secondary">
                Synthesizing final answer...
              </Typography>
            </Box>
          ) : teamRun.finalAnswer ? (
            <MarkdownBlock markdownSx={markdownSx}>{teamRun.finalAnswer}</MarkdownBlock>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Waiting for leader output...
            </Typography>
          )}
        </Box>
      )}

      {members.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ display: "block", mb: 1, fontWeight: 800, color: "text.secondary", textTransform: "uppercase" }}>
            Members
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(auto-fit, minmax(150px, 1fr))" },
              gap: 1,
            }}
          >
            {members.map((member, index) => {
              const status = statusConfig[member.status] || statusConfig.waiting;
              return (
                <Box
                  key={member.memberKey || member.member_agent_id || index}
                  sx={{
                    border: "1px solid",
                    borderColor: member.status === "running" ? "primary.main" : "divider",
                    borderRadius: 1,
                    p: 1,
                    backgroundColor: isDarkMode ? "rgba(255,255,255,0.025)" : "#ffffff",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.75 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.2, fontSize: 13 }} noWrap>
                        {member.name || member.member_name || `Member ${index + 1}`}
                      </Typography>
                      {member.role && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", fontSize: 11 }}>
                          {member.role}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      color={status.color}
                      variant={member.status === "running" ? "filled" : "outlined"}
                      icon={status.icon}
                      label={status.label}
                      sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                    />
                  </Box>

                  {member.toolName && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                      <Build sx={{ fontSize: 13 }} />
                      <Typography variant="caption" sx={{ fontSize: 11 }}>{member.toolName}</Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {members.some((member) => member.content) && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {members
            .filter((member) => member.content)
            .map((member, index) => (
              <Box key={member.memberKey || member.member_agent_id || `response-${index}`}>
                <Typography
                  variant="caption"
                  sx={{ display: "block", mb: 0.5, fontWeight: 800, color: "text.secondary" }}
                >
                  {member.name || member.member_name || `Member ${index + 1}`}
                </Typography>
                <MarkdownBlock markdownSx={markdownSx}>{member.content}</MarkdownBlock>
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
};

export default TeamRunMessage;
